import { Component, DestroyRef, forwardRef, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormBuilder, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, merge, tap } from 'rxjs';

export interface CheckboxOption {
  label: string;
  value: string;
}

@Component({
  selector: 'sd-checkbox-group',
  standalone: false,
  templateUrl: './checkbox-group.component.html',
  styleUrl: './checkbox-group.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxGroupComponent),
      multi: true
    }
  ]
})
export class CheckboxGroupComponent implements ControlValueAccessor {
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);

  formArray = this.fb.array<boolean>([]);
  options$ = new BehaviorSubject<CheckboxOption[]>([]);

  private value$ = new BehaviorSubject<string[]>([]);
  private valueChange$ = this.value$.pipe(distinctUntilChanged(compareStringArrays));
  
  @Input() set options(value: CheckboxOption[]) {
    this.options$.next(value || []);
  }

  ngOnInit() {
    this.effects$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private rebuildFormArrayEffect$ = combineLatest([this.options$, this.valueChange$]).pipe(
    tap(([options, values]) => this.rebuildFormArray(options, values)),
  );

  private emitSelectedValuesEffect$ = this.formArray.valueChanges.pipe(
    map(checkboxValues => checkboxValues.map((isChecked, i) => isChecked ? this.options$.value[i]?.value : null).filter(v => v !== null)),
    distinctUntilChanged(compareStringArrays),
    tap(selectedValues => {
      this.ngOnChange(selectedValues);
      this.ngOnTouched();
    })
  );

  private effects$ = merge(this.rebuildFormArrayEffect$, this.emitSelectedValuesEffect$);
  
  private rebuildFormArray(options: CheckboxOption[], selectedValues: string[]): void {   
    while (this.formArray.length) {
      this.formArray.removeAt(0);
    }

    options.forEach(option => {
      const isSelected = selectedValues.includes(option.value);
      const control = this.fb.control(isSelected);
      this.formArray.push(control);
    });
  }

  // ControlValueAccessor implementation
  private ngOnChange: (value: string[]) => void = () => {};
  private ngOnTouched: () => void = () => {};

  writeValue(value: string[]): void {
    this.value$.next(value || []);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.ngOnChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.ngOnTouched = fn;
  }

}

function compareStringArrays(arr1: string[], arr2: string[]): boolean {
  return arr1.length === arr2.length ? arr1.every(value => arr2.includes(value)) : false;
}
